/**
 * Playgroundタイムラインコンポーネント
 *
 * 処理ステップの結果をタイムライン表示
 */

import {
  Stack,
  Title,
  Text,
  Group,
  Badge,
  Timeline,
  Card,
} from "@mantine/core";
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  Minus,
} from "lucide-react";
import {
  PlaygroundResult,
  PlaygroundStep,
} from "../../services/playgroundService";

// サブコンポーネントをインポート
import { TimelineNormalizeDetails } from "./TimelineNormalizeDetails";
import { TimelineOcrResult } from "./TimelineOcrResult";
import { TimelineCsvPreview } from "./TimelineCsvPreview";
import { TimelineEmailPreview } from "./TimelineEmailPreview";

/**
 * ステップのステータスに応じたアイコンを返す
 */
function getStepIcon(status: PlaygroundStep["status"]) {
  switch (status) {
    case "success":
      return <CheckCircle size={20} color="var(--mantine-color-green-6)" />;
    case "error":
      return <XCircle size={20} color="var(--mantine-color-red-6)" />;
    case "warning":
      return <AlertCircle size={20} color="var(--mantine-color-yellow-6)" />;
    case "skipped":
      return <Minus size={20} color="var(--mantine-color-gray-5)" />;
    default:
      return null;
  }
}

/**
 * ステップのステータスに応じたBadgeカラーを返す
 */
function getStepBadgeColor(status: PlaygroundStep["status"]): string {
  switch (status) {
    case "success":
      return "green";
    case "error":
      return "red";
    case "warning":
      return "yellow";
    case "skipped":
      return "gray";
    default:
      return "blue";
  }
}

interface PlaygroundTimelineProps {
  result: PlaygroundResult;
  onDownloadCsv: () => void;
}

export function PlaygroundTimeline({
  result,
  onDownloadCsv,
}: PlaygroundTimelineProps) {
  return (
    <Card withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={5}>3. 結果</Title>
          <Badge
            size="lg"
            variant="light"
            color={result.success ? "green" : "red"}
            leftSection={
              result.success ? <CheckCircle size={14} /> : <XCircle size={14} />
            }
          >
            {result.success ? "成功" : "一部エラー"}
          </Badge>
        </Group>

        {/* 処理ステップ タイムライン */}
        <Timeline active={result.steps.length - 1} bulletSize={20} lineWidth={2}>
          {result.steps.map((step, index) => {
            // ステップ名に基づいてプレビューを表示するか判定
            const showOcrPreview =
              (step.name === "第2パス: AI OCR解析" || step.name === "AI OCR解析") && result.ocrResult;
            const showCsvPreview =
              step.name.startsWith("CSV生成") && result.csvPreview;
            const showEmailPreview =
              step.name.startsWith("メール設定確認") && result.emailPreview;
            const showNormalizeDetails =
              step.name === "名寄せ処理" && result.normalizeDetails;

            return (
              <Timeline.Item
                key={index}
                bullet={getStepIcon(step.status)}
                color={getStepBadgeColor(step.status)}
                title={
                  <Group gap="xs" align="center">
                    <Text size="sm" fw={700}>
                      {step.name}
                    </Text>
                    {step.durationMs !== undefined && (
                      <Badge
                        size="xs"
                        variant="light"
                        color="blue"
                        leftSection={<Clock size={10} />}
                      >
                        {step.durationMs}ms
                      </Badge>
                    )}
                    <Badge
                      size="xs"
                      variant="light"
                      color={getStepBadgeColor(step.status)}
                    >
                      {step.status === "success"
                        ? "成功"
                        : step.status === "error"
                          ? "エラー"
                          : step.status === "warning"
                            ? "警告"
                            : "スキップ"}
                    </Badge>
                  </Group>
                }
              >
                {step.description && (
                  <Text size="xs" c="dimmed" mt={4}>
                    {step.description}
                  </Text>
                )}

                {/* 名寄せ詳細のプレビュー */}
                {showNormalizeDetails && (
                  <TimelineNormalizeDetails details={result.normalizeDetails!} />
                )}

                {/* OCR結果のプレビュー */}
                {showOcrPreview && (
                  <TimelineOcrResult ocrResult={result.ocrResult!} />
                )}

                {/* CSV結果のプレビュー */}
                {showCsvPreview && (
                  <TimelineCsvPreview
                    csvPreview={result.csvPreview!}
                    onDownload={onDownloadCsv}
                  />
                )}

                {/* メール設定のプレビュー */}
                {showEmailPreview && (
                  <TimelineEmailPreview emailPreview={result.emailPreview!} />
                )}
              </Timeline.Item>
            );
          })}
        </Timeline>
      </Stack>
    </Card>
  );
}
